import { KEYS } from "../hooks";
import { clearCache, invalidate } from "../resource";
import { SessionExpiredError } from "../types";
import { nextCheckDelay } from "./logic/session-clock";
import type { IdentityApi, Session } from "./types";

/**
 * The tab's view of its session, shared by gating, the shell and the session-expired dialog. It re-reads the
 * session when identity reports a change, on a timer at the next expiry (FR-ADMIN-047), and when a call fails with
 * SessionExpiredError. It clears cached data whenever the person changes, so nothing of theirs stays on screen.
 */

export type SessionState =
  | { status: "loading" }
  /** "expired": the session timed out before this page loaded, so sign-in pages can say so. */
  | { status: "signedOut"; reason: "none" | "expired" }
  /** Ended while the page was open: the page stays and a dialog asks to sign in again. */
  | { status: "expired"; session: Session }
  | { status: "active"; session: Session };

export interface SessionStore {
  getSnapshot(): SessionState;
  subscribe(listener: () => void): () => void;
  /** Read the session from the identity service again. */
  refresh(): Promise<void>;
  /** A call failed with SessionExpiredError. */
  markExpired(): void;
  /** After a call that returned the new session (sign-in, terms, switching organisation) or signed out (null). */
  set(session: Session | null): void;
  /** Start listening and timing; returns a stop function. */
  start(): () => void;
}

export interface SessionStoreOptions {
  /** Regular re-check while a session is active, in ms. */
  checkIntervalMs: number;
  now: () => number;
}

/** Fields whose change matters to the UI; the idle end moves with every call and is only used for timing. */
function signature(session: Session): string {
  return JSON.stringify({ ...session, idleExpiresAt: undefined });
}

export function createSessionStore(
  identity: IdentityApi,
  { checkIntervalMs, now }: SessionStoreOptions,
): SessionStore {
  let state: SessionState = { status: "loading" };
  let latest: Session | undefined;
  let generation = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const listeners = new Set<() => void>();

  const emit = (next: SessionState) => {
    const previous = state;
    state = next;
    const before =
      previous.status === "active" || previous.status === "expired" ? previous.session : undefined;
    if (next.status === "active") {
      const after = next.session;
      // A different person, or a fresh sign-in: drop everything loaded (or refused) before.
      if (before === undefined ? previous.status === "signedOut" : after.user.id !== before.user.id)
        clearCache();
      // The same person back after an expiry, or in another organisation (FR-PROJ-091): reload what is on screen.
      else if (previous.status === "expired" || after.organisation?.id !== before?.organisation?.id)
        invalidate("");
      // Same person and organisation, but a name may have changed (profile, organisation setup or settings).
      else if (
        after.user.name !== before?.user.name ||
        after.organisation?.name !== before.organisation?.name
      ) {
        for (const key of [KEYS.me, KEYS.people, KEYS.organisation]) invalidate(key);
      }
    } else if (next.status === "signedOut" && before !== undefined) {
      clearCache();
    }
    for (const listener of listeners) listener();
  };

  const schedule = () => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    if (state.status !== "active" || latest === undefined) return;
    const ends = Math.min(Date.parse(latest.idleExpiresAt), Date.parse(latest.absoluteExpiresAt));
    timer = setTimeout(() => void store.refresh(), nextCheckDelay(ends, now(), checkIntervalMs));
  };

  const setActive = (session: Session) => {
    latest = session;
    if (state.status === "active" && signature(state.session) === signature(session)) return;
    emit({ status: "active", session });
  };

  const store: SessionStore = {
    getSnapshot: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    async refresh() {
      const mine = ++generation;
      try {
        const session = await identity.getSession();
        if (mine !== generation) return;
        if (session === null) {
          latest = undefined;
          if (state.status !== "signedOut") emit({ status: "signedOut", reason: "none" });
        } else {
          setActive(session);
        }
      } catch (error) {
        if (mine !== generation) return;
        if (!(error instanceof SessionExpiredError)) return; // Network trouble: keep what we know.
        store.markExpired();
        if (state.status === "loading") emit({ status: "signedOut", reason: "expired" });
      } finally {
        if (mine === generation) schedule();
      }
    },
    markExpired() {
      if (state.status === "active") emit({ status: "expired", session: state.session });
      schedule();
    },
    set(session) {
      generation++;
      if (session === null) {
        latest = undefined;
        emit({ status: "signedOut", reason: "none" });
      } else {
        setActive(session);
      }
      schedule();
    },
    start() {
      const unsubscribe = identity.subscribe(() => void store.refresh());
      void store.refresh();
      return () => {
        unsubscribe();
        if (timer !== undefined) clearTimeout(timer);
      };
    },
  };
  return store;
}
